/**
 * EARLY ACCESS LOCK — companion to snippets/early-access.liquid.
 *
 * Loaded only while Theme settings → Early Access Lock is enabled.
 *
 * - Unlocked browsers (localStorage key set) behave completely normally.
 * - Locked browsers: every add-to-cart action is intercepted in the
 *   CAPTURE phase (so it fires before any theme handler):
 *     · submit of any form[action*="/cart/add"]  (PDP, quick add,
 *       bundles, drawer upsell forms)
 *     · clicks on the drawer's JS-driven add buttons
 *   The action is stopped and the subscriber popup opens instead.
 * - Submitting the popup posts Shopify's native customer form
 *   (form_type=customer, tag early-access) — subscribers land in
 *   Shopify's customer list with marketing consent and sync onward to
 *   Klaviyo. If Klaviyo's onsite JS is present the email is identified
 *   there too. Then the unlock is stored and the original action is
 *   replayed.
 *
 * NOTE: this is a soft, browser-level gate for a marketing moment — a
 * technical user could bypass it via the cart API. It is not security.
 */
(function () {
  if (window.earlyAccessLoaded) return;
  window.earlyAccessLoaded = true;

  var KEY = 'sapphire_early_access';

  function unlocked() {
    try { return !!localStorage.getItem(KEY); } catch (e) { return false; }
  }
  function storeUnlock(email) {
    try { localStorage.setItem(KEY, JSON.stringify({ email: email, at: new Date().toISOString() })); } catch (e) { /* noop */ }
  }

  if (unlocked()) return; // nothing to do — buttons stay live

  document.documentElement.classList.add('ea-locked');

  var pending = null; // { form } or { button }

  function modal() { return document.querySelector('[data-ea-modal]'); }

  function openModal() {
    var m = modal();
    if (!m) return;
    m.classList.add('is-open');
    var input = m.querySelector('.ea-modal__input');
    if (input) setTimeout(function () { input.focus(); }, 50);
  }

  function closeModal() {
    var m = modal();
    if (m) m.classList.remove('is-open');
  }

  function unlockAndReplay() {
    document.documentElement.classList.remove('ea-locked');
    var p = pending;
    pending = null;
    setTimeout(function () {
      closeModal();
      if (!p) return;
      if (p.form && document.contains(p.form)) {
        if (typeof p.form.requestSubmit === 'function') p.form.requestSubmit();
        else p.form.submit();
      } else if (p.button && document.contains(p.button)) {
        p.button.click();
      }
    }, 1200); // let the success message land first
  }

  /* ---- interception ---- */
  var JS_ADD_SELECTOR = '.drawer_add_to_cart_btn, .cart-drawer__collection .btn-add';

  document.addEventListener('submit', function (e) {
    if (unlocked()) return;
    var form = e.target;
    if (form && form.closest && form.closest('[data-ea-form]')) return; // our own form
    var action = (form.getAttribute('action') || '');
    if (action.indexOf('/cart/add') === -1) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    pending = { form: form };
    openModal();
  }, true);

  document.addEventListener('click', function (e) {
    if (unlocked()) return;
    var btn = e.target.closest ? e.target.closest(JS_ADD_SELECTOR) : null;
    if (!btn) return;
    // Drawer upsell "Add" inside a form is caught by the submit handler;
    // this catches the fetch-driven recommendation buttons.
    if (btn.closest('form[action*="/cart/add"]')) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    pending = { button: btn };
    openModal();
  }, true);

  /* ---- popup wiring ---- */
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-ea-close]')) closeModal();
  });

  // Access-code unlock — same effect as subscribing, no email needed.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form && form.matches && form.matches('[data-ea-code-form]'))) return;
    e.preventDefault();
    var m = modal();
    var want = (m.getAttribute('data-ea-code') || '').trim().toUpperCase();
    var got = (form.querySelector('input[name="code"]').value || '').trim().toUpperCase();
    var err = m.querySelector('[data-ea-code-error]');
    if (!want || got !== want) {
      err.style.display = 'block';
      return;
    }
    err.style.display = 'none';
    storeUnlock('code:' + got);
    m.classList.add('is-done');
    unlockAndReplay();
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form && form.matches && form.matches('[data-ea-form]'))) return;
    e.preventDefault();

    var m = modal();
    var input = form.querySelector('.ea-modal__input');
    var errorEl = m.querySelector('[data-ea-error]');
    var submitBtn = form.querySelector('.ea-modal__submit');
    var email = (input.value || '').trim();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errorEl.style.display = 'block';
      input.focus();
      return;
    }
    errorEl.style.display = 'none';
    submitBtn.disabled = true;

    // Klaviyo client subscription — captcha-free (the store's Shopify
    // /contact form rejects AJAX posts with "Missing CAPTCHA token").
    // Company id is read from the Klaviyo onsite script tag; the list id
    // comes from Theme settings → Early Access Lock.
    var listId = m.getAttribute('data-ea-list') || '';
    var companyId = '';
    var klScript = document.querySelector('script[src*="klaviyo.js"]');
    if (klScript) companyId = (klScript.src.match(/company_id=([A-Za-z0-9]+)/) || [])[1] || '';

    var subscribe;
    if (companyId && listId) {
      subscribe = fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + companyId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email, properties: { 'Early Access Sale': true } },
                },
              },
              custom_source: 'Early Access Lock',
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }),
      });
    } else {
      subscribe = Promise.resolve();
    }

    subscribe
      .catch(function (err) { console.error('[early-access] subscribe failed', err); })
      .then(function () {
        // Onsite identify too, so the session is tied to the profile.
        try {
          var k = window.klaviyo || window._learnq;
          if (k && typeof k.push === 'function') k.push(['identify', { $email: email }]);
          else if (k && typeof k.identify === 'function') k.identify({ $email: email });
        } catch (err) { /* noop */ }

        storeUnlock(email);
        m.classList.add('is-done');
        unlockAndReplay();
      });
  });
})();
